// src/components/schedule/delivery/deliveryUtils.js
// Pure utility functions and constants for the delivery schedule page.
// Extracted so sub-components can import without pulling in the entire page.

import {
    startOfWeek, endOfWeek, format, addWeeks, addDays,
    startOfDay, isBefore, isToday,
} from 'date-fns';
import { FiHome, FiLayers, FiPackage } from 'react-icons/fi';

// ─── Constants ────────────────────────────────────────────────────────────────

export const DAYS = [
    { label: 'Mon', key: 'Monday' },
    { label: 'Tue', key: 'Tuesday' },
    { label: 'Wed', key: 'Wednesday' },
    { label: 'Thu', key: 'Thursday' },
    { label: 'Fri', key: 'Friday' },
    { label: 'Sat', key: 'Saturday' },
];

export const TIME_SLOTS = [
    'First Delivery',
    'Second Delivery',
    'Before Lunch',
    'Lunchtime',
    'After Lunch',
    'Last Delivery',
];

export const TIME_SLOT_ORDER = {
    'First Delivery': 1,
    'Second Delivery': 2,
    'Before Lunch': 3,
    'Lunchtime': 4,
    'After Lunch': 5,
    'Last Delivery': 6,
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

export const getAustralianDate = () =>
    new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Melbourne' }));

export const getWeekStart = (weekOffset) => {
    const today = getAustralianDate();
    return addWeeks(startOfWeek(today, { weekStartsOn: 1 }), weekOffset);
};

export const getWeekLabel = (weekStart) => {
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    return `${format(weekStart, 'dd MMM')} – ${format(weekEnd, 'dd MMM yyyy')}`;
};

export const getMonthLabel = (weekStart) => format(weekStart, 'MMMM yyyy');

export const getDayDate = (weekStart, dayIndex) =>
    format(addDays(weekStart, dayIndex), 'dd/MM/yyyy');

export const getDayDateObject = (weekStart, dayIndex) => {
    const d = addDays(weekStart, dayIndex);
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0));
};

export const isDateInPast = (date) =>
    isBefore(startOfDay(date), startOfDay(getAustralianDate()));

export const isDayToday = (weekStart, dayIndex) =>
    isToday(getDayDateObject(weekStart, dayIndex));

/** filterDate for react-datepicker — disables Sundays */
export const isDateDisabled = (date) => date.getDay() !== 0;

// ─── Quote type styles ────────────────────────────────────────────────────────

export const getQuoteTypeStyle = (quoteType) => {
    switch (quoteType) {
        case 'roof':   return { icon: FiHome,    bgColor: 'bg-blue-100',  textColor: 'text-blue-700',  label: 'Roof' };
        case 'wall':   return { icon: FiLayers,  bgColor: 'bg-red-100',   textColor: 'text-red-700',   label: 'Wall' };
        case 'floor':  return { icon: FiLayers,  bgColor: 'bg-green-100', textColor: 'text-green-700', label: 'Floor' };
        case 'timber': return { icon: FiPackage, bgColor: 'bg-amber-100', textColor: 'text-amber-700', label: 'Timber' };
        default:       return { icon: FiPackage, bgColor: 'bg-gray-100',  textColor: 'text-gray-700',  label: quoteType };
    }
};

export const getMainStatusColor = (status) => {
    if (status === 'ready_for_delivery') return 'bg-teal-500';
    if (status === 'completed')          return 'bg-gray-500';
    return 'bg-gray-400';
};

// ─── Production status ────────────────────────────────────────────────────────

/**
 * job_production.phase is the authoritative source (updated by the production schedule).
 * job_quotes.production_status may lag; check job_production first.
 */
export const getEffectiveProductionStatus = (assignment) => {
    const prod = assignment?.production;
    if (prod?.is_completed === true || prod?.phase === 'completed') return 'completed';
    if (prod?.phase) return prod.phase;
    return assignment?.quote_info?.production_status || 'not_started';
};

// ─── Status display ───────────────────────────────────────────────────────────

export const formatStatus = (status) => {
    if (!status) return 'Pending';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/** Returns the dominant "next blocker" status label for a card's progress bar. */
export const CardGeneralStatusText = (assignment) => {
    const quote = assignment?.quote_info;
    if (!quote) return 'No Quote';

    const { design_status: design, estimation_status: estimation } = quote;
    const productionStatus = getEffectiveProductionStatus(assignment);
    const deliveryStatus = quote.delivery_status || 'not_scheduled';

    if (design !== 'completed' && design !== 'not_required')
        return `Design ${formatStatus(design)}`;

    if (estimation !== 'completed' && estimation !== 'not_required' &&
        assignment.job_type?.toLowerCase()?.includes('estimation'))
        return `Estimation ${formatStatus(estimation)}`;

    if (productionStatus !== 'completed' && productionStatus !== 'not_required')
        return `Production ${formatStatus(productionStatus)}`;

    if (deliveryStatus !== 'delivered')
        return `Delivery ${formatStatus(deliveryStatus)}`;

    return 'Completed';
};

export const getStatusDot = (status) => {
    switch (status) {
        case 'completed': case 'delivered':
            return 'bg-green-500';
        case 'in_progress': case 'in_transit': case 'in_cutting': case 'in_production':
            return 'bg-yellow-500';
        case 'scheduled': case 'cutting_ended':
            return 'bg-blue-500';
        case 'waiting_assignment': case 'waiting_design': case 'not_scheduled': case 'not_started':
            return 'bg-red-500';
        case 'not_required':
            return 'bg-gray-400';
        case 'cancelled':
            return 'bg-gray-600';
        default:
            return 'bg-gray-400';
    }
};

const _getColor = (status) => {
    switch (status) {
        case 'waiting_assignment': case 'waiting_design':
        case 'not_started': case 'not_scheduled':
            return { bar: 'bg-gray-300', text: 'text-gray-600' };
        case 'in_progress': case 'in_cutting': case 'in_production': case 'in_transit':
        case 'scheduled':
            return { bar: 'bg-yellow-400', text: 'text-yellow-700' };
        case 'completed': case 'delivered':
            return { bar: 'bg-green-500', text: 'text-green-700' };
        case 'cancelled':
            return { bar: 'bg-red-500', text: 'text-red-700' };
        case 'not_required':
            return { bar: 'bg-green-200', text: 'text-green-600' };
        default:
            return { bar: 'bg-gray-200', text: 'text-gray-500' };
    }
};

/** Returns the bar/text colour class for the status progress indicator. */
export const CardGeneralStatusStyle = (delivery, quote, type = 'bar') => {
    if (!quote) return type === 'bar' ? 'bg-gray-300' : 'text-gray-500';

    const { design_status: design, estimation_status: estimation, production_status: production } = quote;
    const deliveryStatus = delivery?.delivery_status || 'not_scheduled';

    if (design !== 'completed'     && design !== 'not_required')      return _getColor(design)[type];
    if (estimation !== 'completed' && estimation !== 'not_required')  return _getColor(estimation)[type];
    if (production !== 'completed' && production !== 'not_required')  return _getColor(production)[type];
    if (deliveryStatus !== 'delivered')                                return _getColor(deliveryStatus)[type];
    return _getColor('delivered')[type];
};

// ─── Sort / filter ────────────────────────────────────────────────────────────

export const sortByTimeSlot = (assignments) =>
    [...assignments].sort((a, b) => {
        const orderA = TIME_SLOT_ORDER[a.quote_info?.delivery_time_slot] ?? 999;
        const orderB = TIME_SLOT_ORDER[b.quote_info?.delivery_time_slot] ?? 999;
        if (orderA !== orderB) return orderA - orderB;

        const aComp = getEffectiveProductionStatus(a) === 'completed';
        const bComp = getEffectiveProductionStatus(b) === 'completed';
        if (aComp && !bComp) return -1;
        if (!aComp && bComp)  return 1;
        return 0;
    });

export const matchesSearch = (assignment, query) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
        assignment.unique_id?.toLowerCase().includes(q) ||
        assignment.quote_info?.childUniqueId?.toLowerCase().includes(q) ||
        assignment.customer_name?.toLowerCase().includes(q) ||
        assignment.customer_address?.toLowerCase().includes(q) ||
        assignment.quote_info?.quote_type?.toLowerCase().includes(q) ||
        assignment.job_deliveries?.some(d => d.assigned_driver_name?.toLowerCase().includes(q)) ||
        assignment.id?.toLowerCase().includes(q)
    );
};
